import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { customersApi, ordersApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import StateCitySelect from '@/components/common/StateCitySelect'

const BLANK = { address: '', state: '', city: '', pincode: '', contact_name: '', contact_phone: '' }

/**
 * Where this order goes: one of the customer's saved addresses, or a new one
 * added to their record and chosen in the same step. For an order staff
 * entered, or one the customer asked to redirect; until it ships.
 */
export default function ShipToPicker({ order, onClose }) {
  const queryClient = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState(BLANK)
  const [error, setError] = useState('')

  const { data: customer } = useQuery({
    queryKey: ['customer', String(order.customer_id)],
    queryFn: () => customersApi.get(order.customer_id),
  })
  const addresses = customer?.shipping_addresses ?? []
  const chosenId =
    order.shipping_address_id ?? addresses.find((a) => a.is_default)?.id ?? null

  const done = (updated) => {
    queryClient.setQueryData(['order', String(order.id)], updated)
    queryClient.invalidateQueries({ queryKey: ['orders'] })
    onClose()
  }

  const choose = useMutation({
    mutationFn: (addressId) => ordersApi.setShippingAddress(order.id, addressId),
    onSuccess: done,
    onError: (e) => setError(errorMessage(e)),
  })

  // Saved to the customer first, so it is in their address book for next time,
  // then chosen for this order.
  const addAndChoose = useMutation({
    mutationFn: async () => {
      const before = new Set(addresses.map((a) => a.id))
      const fresh = await customersApi.addAddress(order.customer_id, {
        ...draft,
        is_default: addresses.length === 0,
      })
      queryClient.setQueryData(['customer', String(order.customer_id)], (c) =>
        c ? { ...c, shipping_addresses: fresh } : c,
      )
      const added = fresh.find((a) => !before.has(a.id))
      return ordersApi.setShippingAddress(order.id, added.id)
    },
    onSuccess: done,
    onError: (e) => setError(errorMessage(e)),
  })

  const busy = choose.isPending || addAndChoose.isPending

  return (
    <div className="ship-to-picker">
      {error && <p className="field-error">{error}</p>}

      {!adding && (
        <>
          {addresses.length === 0 && <p className="muted">No saved addresses yet.</p>}
          <div className="ship-to-list" role="radiogroup" aria-label="Ship to">
            {addresses.map((a) => {
              const on = a.id === chosenId
              return (
                <button
                  type="button"
                  key={a.id}
                  role="radio"
                  aria-checked={on}
                  className={`ship-to-option${on ? ' on' : ''}`}
                  disabled={busy}
                  onClick={() => (on ? onClose() : choose.mutate(a.id))}
                >
                  <span className="pre-line">{a.address}</span>
                  <span className="muted">
                    {[a.city, a.state].filter(Boolean).join(', ')}
                    {a.pincode ? ` - ${a.pincode}` : ''}
                    {a.is_default ? ' · default' : ''}
                  </span>
                  {(a.contact_name || a.contact_phone) && (
                    <span className="muted">
                      Contact: {[a.contact_name, a.contact_phone].filter(Boolean).join(' · ')}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
          <div className="transport-edit-actions">
            <button type="button" className="btn btn-sm" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => {
                setDraft(BLANK)
                setError('')
                setAdding(true)
              }}
            >
              + Add new address
            </button>
          </div>
        </>
      )}

      {adding && (
        <form
          className="transport-edit"
          onSubmit={(e) => {
            e.preventDefault()
            setError('')
            addAndChoose.mutate()
          }}
        >
          <label className="field">
            <span>Address *</span>
            <textarea
              rows={3}
              required
              value={draft.address}
              placeholder="Door no, street, area"
              onChange={(e) => setDraft({ ...draft, address: e.target.value })}
            />
          </label>
          <StateCitySelect
            state={draft.state}
            city={draft.city}
            onChange={({ state, city }) => setDraft((d) => ({ ...d, state, city }))}
          />
          <label className="field">
            <span>Pincode</span>
            <input
              inputMode="numeric"
              value={draft.pincode}
              onChange={(e) => setDraft({ ...draft, pincode: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Contact name</span>
            <input
              value={draft.contact_name}
              onChange={(e) => setDraft({ ...draft, contact_name: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Contact phone</span>
            <input
              type="tel"
              inputMode="tel"
              value={draft.contact_phone}
              onChange={(e) => setDraft({ ...draft, contact_phone: e.target.value })}
            />
          </label>
          <div className="transport-edit-actions">
            <button type="button" className="btn btn-sm" onClick={() => setAdding(false)}>
              Back
            </button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !draft.address.trim()}>
              {addAndChoose.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
